import { Component, EventEmitter, inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { MeterDetailModel } from 'src/app/models/maintenance/meter-detail.model';
import { MeterModel } from 'src/app/models/maintenance/meter.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { MeterDetailCaptureTableModel } from 'src/app/models/tables/maintenance/meter-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MeterDetailService } from 'src/app/services/maintenance/meter-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-meter-detail',
  templateUrl: './meter-detail.component.html',
  styleUrl: './meter-detail.component.scss',
  standalone: false,
})
export class MeterDetailComponent {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly meterDetailService = inject(MeterDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() disabledDetail: boolean = false;
  @Input() permissionUpdatable: boolean = false;
  @Input() meterModel: MeterModel = new MeterModel();
  @Output() emitQuantityDetails = new EventEmitter<number>();
  @Output() hasQuantityInZero = new EventEmitter<boolean>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  componentTableSelection = CustomTableSelectComponent;
  dataArticle: PagedResponseModel<ArticleSelectionTableModel> =
    new PagedResponseModel<ArticleSelectionTableModel>();
  detailData: PagedResponseModel<MeterDetailCaptureTableModel> =
    new PagedResponseModel<MeterDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: []
  });
  isFormView: boolean = false;
  limit: number = 5;
  limitArticle: number = 5;
  meterDetail: MeterDetailModel = new MeterDetailModel();
  meterDetails: MeterDetailModel[] = [];
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [];

  async ngOnInit() {
    this.tableActions = this.getDefaultActions();
    await this.getColumnsDetailsForCapture();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const { disabledDetail, meterModel } = changes;
    if (meterModel?.firstChange === false) {
      this.getFormView(false);
      this.clearSelectionTrigger++;
      await this.getDetailsForCapture();
    }

    if (disabledDetail?.firstChange === false) {
      this.tableActions = this.getDefaultActions();
    }
  }

  async deleteMeterDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el detalle del medidor?');
    if (accepted) {
      try {
        await firstValueFrom(this.meterDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle del medidor fue eliminado exitosamente.'
        );
        this.clearSelectionTrigger++;
        await this.getDetailsForCapture();
        this.getFormView(false);
      } catch (error) {
        console.error('Error al eliminar el detalle del medidor.', error);
      }
    }
  }

  async getArticlesForSelection() {
    try {
      const response = await firstValueFrom(
        this.meterDetailService.getArticlesForSelection(
          this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle
        )
      );
      this.dataArticle = response;
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos.', error);
    }
  }

  async getColumnsArticlesForSelection() {
    try {
      if (this.headerArticle.length === 0) {
        const response = await firstValueFrom(
          this.meterDetailService.getColumnsArticlesForSelection()
        );
        this.headerArticle = response;
        this.updateInputs();
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsDetailsForCapture() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(
          this.meterDetailService.getColumnsDetailsForCapture()
        );
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disabledDetail) {
      return [];
    } else {
      return [
        {
          label: 'Reiniciar',
          icon: 'pi pi-sync',
          action: 'reset'
        },
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  async getDetailsForCapture() {
    try {
      const response = await firstValueFrom(
        this.meterDetailService.getDetailsForCapture(
          this.page, this.limit, this.columnSort, this.ascendant, this.meterModel.meterId
        )
      );
      this.detailData = response;
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.detailData.totalItems);
      this.hasQuantityInZero.emit(this.detailData.items.some(detail => detail.captureQuantity === 0));
    } catch (error) {
      console.error('Error al obtener los detalles de la solución para la captura de datos.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async getMeterDetailById(meterDetailId: number) {
    try {
      const response = await firstValueFrom(
        this.meterDetailService.getById(meterDetailId)
      );
      this.meterDetail = response;
      this.getFormView(true);
    } catch (error) {
      console.error('Error al obtener el detalle por id.', error);
    }
  }

  async getMeterDetailByKeys(articleId: number) {
    try {
      const meterId = this.meterModel.meterId;
      const response = await firstValueFrom(
        this.meterDetailService.getMeterDetailByKeys(meterId, articleId)
      );
      this.meterDetail = response;
      this.getFormView(true);
    } catch (error) {
      console.error('Error al obtener el detalle del traslado.', error);
    }
  }

  async handleSelectedItem(selectedItem: any[]) {
    if (selectedItem.length === 1) {
      await this.getMeterDetailByKeys(selectedItem[0].articleId);
      return;
    }

    this.meterDetails = selectedItem.map(({ meterDetailId, articleId }) => ({
      ...new MeterDetailModel(),
      meterId: this.meterModel.meterId,
      meterDetailId,
      articleId
    }));
  }

  async onAction(event: { action: string, item?: MeterDetailCaptureTableModel, selectedItems?: number[] }) {
    switch (event.action) {
      case 'edit': {
        await this.getMeterDetailById(event.item!.id);
        break;
      }
      case 'reset': {
        await this.resetHourMeter(event.item!.id);
        break;
      }
      case 'bulk-delete': {
        if (event.selectedItems) {
          await this.deleteMeterDetail(event.selectedItems);
        }
        break;
      }
    }
  }

  async openArticleSelectionModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para la edición de medidores. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.meterModel.meterId) {
      this.toastHandlerService.showToast(
        'warning',
        `El medidor aún no se ha guardado.`
      );
      return;
    }
    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsArticlesForSelection(),
      this.getArticlesForSelection()
    ]);
    this.articleSelectionModalComponent?.show();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getDetailsForCapture();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    await this.getArticlesForSelection();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async resetHourMeter(meterDetailId: number) {
    try {
      const response = await firstValueFrom(
        this.meterDetailService.resetHourMeter(meterDetailId)
      );
      this.toastHandlerService.showToast(
        'success',
        `El detalle del medidor fue reiniciado correctamente.`
      );
      this.meterDetail = response;
      await this.getDetailsForCapture();
    } catch (error) {
      console.error('Error al reiniciar el detalle del medidor.', error);
    }
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }
}
